'use client';

import React, { useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { PrimaryButton, SecondaryButton, DangerButton } from '@/components/platform/buttons';
import { Input } from '@/components/platform/forms';

interface CreateItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (name: string) => Promise<void>;
  title: string;
  fieldLabel: string;
  placeholder?: string;
  description?: string;
  // Edit mode props (optional)
  editMode?: boolean;
  initialValue?: string;
  onDelete?: () => Promise<void>;
}

export default function CreateItemModal({
  isOpen,
  onClose,
  onCreate,
  title,
  fieldLabel,
  placeholder = '',
  description,
  editMode = false,
  initialValue = '',
  onDelete,
}: CreateItemModalProps) {
  const { darkMode } = useDarkMode();
  const [name, setName] = useState(initialValue);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset form when modal opens/closes (adjust-state-during-render pattern,
  // https://react.dev/learn/you-might-not-need-an-effect)
  const [prevSync, setPrevSync] = useState({ isOpen, editMode, initialValue });
  if (
    prevSync.isOpen !== isOpen ||
    prevSync.editMode !== editMode ||
    prevSync.initialValue !== initialValue
  ) {
    setPrevSync({ isOpen, editMode, initialValue });
    setName(isOpen && editMode ? initialValue : '');
    setError('');
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('This field is required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await onCreate(name.trim());
      setName('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create item');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && !deleteLoading) {
      setName('');
      setError('');
      onClose();
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setDeleteLoading(true);
    setError('');
    try {
      await onDelete();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-[110]">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/75 backdrop-blur-sm transition-opacity duration-300 ease-out data-closed:opacity-0"
      />

      <div className="fixed inset-0 z-[110] w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel
            transition
            className={`relative w-full max-w-lg transform overflow-hidden rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transition-all duration-300 ease-out data-closed:scale-95 data-closed:opacity-0 sm:p-6 ${
              darkMode ? 'border border-white/10 bg-gray-800' : 'bg-white'
            }`}
          >
            {/* Close Button */}
            <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className={`rounded-md focus:ring-2 focus:ring-indigo-500 focus:outline-hidden ${
                  darkMode
                    ? 'bg-gray-800 text-gray-400 hover:text-gray-300'
                    : 'bg-white text-gray-400 hover:text-gray-500'
                }`}
              >
                <span className="sr-only">Close</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>

            {/* Header */}
            <div className="mb-6">
              <DialogTitle
                className={`text-base font-semibold ${
                  darkMode ? 'text-gray-100' : 'text-gray-900'
                }`}
              >
                {title}
              </DialogTitle>
              {description && (
                <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {description}
                </p>
              )}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <Input
                  label={fieldLabel}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={placeholder}
                  error={error}
                  required
                  disabled={loading}
                />
              </div>

              {/* Actions */}
              <div
                className={`flex gap-3 ${editMode && onDelete ? 'justify-between' : 'justify-end'}`}
              >
                {editMode && onDelete && (
                  <DangerButton
                    type="button"
                    onClick={handleDelete}
                    disabled={loading || deleteLoading}
                    loading={deleteLoading}
                  >
                    Delete
                  </DangerButton>
                )}
                <div className="flex gap-3">
                  <SecondaryButton
                    type="button"
                    onClick={handleClose}
                    disabled={loading || deleteLoading}
                  >
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton type="submit" loading={loading} disabled={deleteLoading}>
                    {editMode ? 'Save' : 'Create'}
                  </PrimaryButton>
                </div>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
